import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import Navbar from '../components/Navbar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Users, Search, TrendingUp, DollarSign, Star, MessageSquare, 
  BarChart3, Activity, Loader2, RefreshCw, Shield, Calendar, Mail, Send,
  ThumbsUp, ThumbsDown, Bug, Filter, LogIn, Eye, EyeOff, Bot, LineChart, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

// Import extracted admin components
import { 
  AdminManagementPanel, 
  FeedbackPanel, 
  AIQueryPanel, 
  StatCard, 
  SimpleBarChart 
} from '../components/admin';

// Keep TimeSeriesPanel inline for now (complex with chart dependencies)

// Time Series Analytics Panel Component
const TimeSeriesPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');

  useEffect(() => {
    loadData();
  }, [period]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.getAdminTimeSeries(period);
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load time series data');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (title, chartData) => {
    const text = `${title}\n${'-'.repeat(40)}\n` + 
      chartData.map(d => `${d.date}: ${JSON.stringify(d)}`).join('\n');
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const copyChartAsTable = (title, chartData, columns) => {
    const header = columns.join('\t');
    const rows = chartData.map(d => columns.map(col => d[col] ?? '').join('\t')).join('\n');
    const text = `${title}\n${header}\n${rows}`;
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-sky-600" />
      </div>
    );
  }

  // Calculate max values for scaling
  const maxUsers = Math.max(...(data?.daily_activity?.map(d => d.active_users) || [1]), 1);
  const maxSearches = Math.max(...(data?.daily_activity?.map(d => d.search_count) || [1]), 1);
  const maxCumulative = Math.max(...(data?.cumulative_users?.map(d => d.total_users) || [1]), 1);

  // Determine how many bars to show based on data length
  const activityData = data?.daily_activity || [];
  const cumulativeData = data?.cumulative_users || [];
  const feedbackData = data?.daily_feedback || [];

  return (
    <div className="space-y-6">
      {/* Time Range Selector */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">Time Range:</span>
          {[
            { value: 'week', label: 'Last Week' },
            { value: 'month', label: 'Last Month' },
            { value: 'year', label: 'Last Year' },
            { value: 'all', label: 'All Time' }
          ].map(option => (
            <Button
              key={option.value}
              variant={period === option.value ? "default" : "outline"}
              size="sm"
              onClick={() => setPeriod(option.value)}
              className="rounded-full"
            >
              {option.label}
            </Button>
          ))}
        </div>
        <Button variant="ghost" size="sm" onClick={loadData} className="rounded-full">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* Summary Stats */}
      {data?.summary && (
        <div className="grid grid-cols-4 gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-sky-600">{data.summary.total_searches}</p>
              <p className="text-xs text-slate-500">Total Searches</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-purple-600">{data.summary.total_users}</p>
              <p className="text-xs text-slate-500">Total Users</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-emerald-600">{data.summary.total_feedback}</p>
              <p className="text-xs text-slate-500">Feedback Received</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-600">{data.summary.avg_rating || '-'}</p>
              <p className="text-xs text-slate-500">Avg Rating</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Daily Activity Chart */}
      <Card className="border-slate-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Activity className="h-5 w-5 text-sky-500" />
                Daily Activity
              </CardTitle>
              <CardDescription>
                Active users and searches per day ({data?.period_label || period})
              </CardDescription>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => copyChartAsTable('Daily Activity', activityData, ['date', 'active_users', 'search_count'])}
              className="rounded-full"
              title="Copy to clipboard"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {activityData.length === 0 ? (
            <p className="text-center text-slate-400 py-8">No activity data for this period</p>
          ) : (
            <>
              <div className="h-48 flex items-end justify-center gap-4 overflow-hidden px-4">
                {activityData.map((day, i) => (
                  <div key={i} className="w-16 flex flex-col items-center gap-1 group relative">
                    <div 
                      className="w-full bg-sky-500 rounded-t transition-all hover:bg-sky-600"
                      style={{ height: `${Math.max((day.active_users / maxUsers) * 150, 8)}px` }}
                    />
                    <div 
                      className="w-full bg-emerald-500 rounded-t transition-all hover:bg-emerald-600"
                      style={{ height: `${Math.max((day.search_count / maxSearches) * 100, 8)}px` }}
                    />
                    <span className="text-xs text-slate-500 mt-1">{day.date.slice(5)}</span>
                    <div className="absolute bottom-full mb-2 hidden group-hover:block bg-slate-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                      {day.date}: {day.active_users} users, {day.search_count} searches
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-center gap-6 mt-3 text-xs">
                <span className="flex items-center gap-1"><div className="w-3 h-3 bg-sky-500 rounded" /> Active Users (max: {maxUsers})</span>
                <span className="flex items-center gap-1"><div className="w-3 h-3 bg-emerald-500 rounded" /> Searches (max: {maxSearches})</span>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Cumulative Users */}
      <Card className="border-slate-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="h-5 w-5 text-purple-500" />
                Cumulative User Growth
              </CardTitle>
              <CardDescription>Total registered users over time</CardDescription>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => copyChartAsTable('Cumulative User Growth', cumulativeData, ['date', 'new_users', 'total_users'])}
              className="rounded-full"
              title="Copy to clipboard"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {cumulativeData.length === 0 ? (
            <p className="text-center text-slate-400 py-8">No user growth data for this period</p>
          ) : (
            <>
              <div className="h-32 flex items-end justify-center gap-4 overflow-hidden px-4">
                {cumulativeData.map((day, i) => (
                  <div key={i} className="w-16 flex flex-col items-center group relative">
                    <div 
                      className="w-full bg-purple-500 rounded-t transition-all hover:bg-purple-600"
                      style={{ height: `${Math.max((day.total_users / maxCumulative) * 100, 8)}px` }}
                    />
                    <span className="text-xs text-slate-500 mt-1">{day.date.slice(5)}</span>
                    <div className="absolute bottom-full mb-2 hidden group-hover:block bg-slate-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                      {day.date}: {day.total_users} total (+{day.new_users} new)
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-center mt-2">
                <span className="text-sm font-medium text-purple-600">
                  {cumulativeData[cumulativeData.length - 1]?.total_users} total users
                </span>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Feedback Trends */}
      <Card className="border-slate-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Star className="h-5 w-5 text-amber-500" />
                Feedback Over Time
              </CardTitle>
              <CardDescription>Ratings and feedback trends</CardDescription>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => copyChartAsTable('Feedback Over Time', feedbackData, ['date', 'count', 'avg_app_rating', 'thumbs_up', 'thumbs_down'])}
              className="rounded-full"
              title="Copy to clipboard"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {feedbackData.length === 0 ? (
            <p className="text-center text-slate-400 py-8">No feedback data for this period</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {feedbackData.map((day, i) => (
                <div key={i} className="flex items-center gap-3 text-sm p-2 hover:bg-slate-50 rounded">
                  <span className="w-24 text-slate-500 font-mono">{day.date}</span>
                  <div className="flex-1 flex items-center gap-3">
                    {day.avg_app_rating > 0 && (
                      <span className="flex items-center gap-1 text-amber-600 bg-amber-50 px-2 py-0.5 rounded">
                        <Star className="h-3 w-3" /> {day.avg_app_rating}
                      </span>
                    )}
                    {day.thumbs_up > 0 && (
                      <span className="flex items-center gap-1 text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded">
                        <ThumbsUp className="h-3 w-3" /> {day.thumbs_up}
                      </span>
                    )}
                    {day.thumbs_down > 0 && (
                      <span className="flex items-center gap-1 text-red-500 bg-red-50 px-2 py-0.5 rounded">
                        <ThumbsDown className="h-3 w-3" /> {day.thumbs_down}
                      </span>
                    )}
                  </div>
                  <span className="text-slate-400 text-xs">{day.count} total</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const AdminDashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  const [overview, setOverview] = useState(null);
  const [userAnalytics, setUserAnalytics] = useState(null);
  const [searchAnalytics, setSearchAnalytics] = useState(null);
  const [topicAnalytics, setTopicAnalytics] = useState(null);
  const [engagementAnalytics, setEngagementAnalytics] = useState(null);
  const [retentionAnalytics, setRetentionAnalytics] = useState(null);

  useEffect(() => {
    // Only load data if user is admin
    if (user?.is_admin) {
      loadAllData();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [overviewRes, usersRes, searchesRes, topicsRes, engagementRes, retentionRes] = await Promise.all([
        api.getAdminOverview(),
        api.getAdminUserAnalytics(30),
        api.getAdminSearchAnalytics(30),
        api.getAdminTopicAnalytics(),
        api.getAdminEngagementAnalytics(30),
        api.getAdminRetentionAnalytics()
      ]);
      
      setOverview(overviewRes.data);
      setUserAnalytics(usersRes.data);
      setSearchAnalytics(searchesRes.data);
      setTopicAnalytics(topicsRes.data);
      setEngagementAnalytics(engagementRes.data);
      setRetentionAnalytics(retentionRes.data);
    } catch (err) {
      console.error('Failed to load analytics:', err);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAllData();
    setRefreshing(false);
    toast.success('Analytics refreshed');
  };

  if (!user?.is_admin) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="max-w-4xl mx-auto px-4 py-12 text-center">
            <Shield className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h1 className="text-2xl font-semibold text-slate-900 mb-2">
              {user ? 'Admin Access Required' : 'Please Log In'}
            </h1>
            <p className="text-slate-500 mb-6">
              {user 
                ? "You don't have permission to view this page." 
                : "You need to be logged in as an admin to access this page."}
            </p>
            {!user && (
              <Button 
                onClick={() => navigate('/login')}
                className="rounded-full bg-slate-900 hover:bg-slate-800"
                data-testid="admin-login-btn"
              >
                <LogIn className="h-4 w-4 mr-2" />
                Log In
              </Button>
            )}
          </div>
        </main>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <Navbar />
        <main className="pt-20 pb-16 flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <main className="pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-slate-900 flex items-center justify-center">
                <BarChart3 className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold text-slate-900">Admin Dashboard</h1>
                <p className="text-sm text-slate-500">Analytics & Insights</p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={handleRefresh}
              disabled={refreshing}
              className="rounded-full"
              data-testid="refresh-analytics-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* Overview Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard 
              title="Total Users" 
              value={overview?.users?.total || 0}
              subtitle={`+${overview?.users?.this_week || 0} this week`}
              icon={Users}
              color="sky"
            />
            <StatCard 
              title="Total Searches" 
              value={overview?.queries?.total || 0}
              subtitle={`${overview?.queries?.avg_per_user || 0} avg/user`}
              icon={Search}
              color="emerald"
            />
            <StatCard 
              title="Active Users (7d)" 
              value={overview?.active_users?.this_week || 0}
              subtitle={`${overview?.active_users?.today || 0} today`}
              icon={Activity}
              color="amber"
            />
            <StatCard 
              title="Total Revenue" 
              value={`$${overview?.engagement?.total_revenue || 0}`}
              subtitle={`${overview?.engagement?.donations || 0} donations`}
              icon={DollarSign}
              color="green"
            />
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-slate-100 rounded-full p-1 mb-6">
              <TabsTrigger value="overview" className="rounded-full px-4" data-testid="tab-overview">
                Overview
              </TabsTrigger>
              <TabsTrigger value="users" className="rounded-full px-4" data-testid="tab-users">
                Users
              </TabsTrigger>
              <TabsTrigger value="searches" className="rounded-full px-4" data-testid="tab-searches">
                Searches
              </TabsTrigger>
              <TabsTrigger value="topics" className="rounded-full px-4" data-testid="tab-topics">
                Topics
              </TabsTrigger>
              <TabsTrigger value="engagement" className="rounded-full px-4" data-testid="tab-engagement">
                Engagement
              </TabsTrigger>
              <TabsTrigger value="retention" className="rounded-full px-4" data-testid="tab-retention">
                Retention
              </TabsTrigger>
              <TabsTrigger value="alerts" className="rounded-full px-4" data-testid="tab-alerts">
                <Mail className="h-4 w-4 mr-1" />
                Alerts
              </TabsTrigger>
              <TabsTrigger value="admins" className="rounded-full px-4" data-testid="tab-admins">
                <Shield className="h-4 w-4 mr-1" />
                Admins
              </TabsTrigger>
              <TabsTrigger value="feedback" className="rounded-full px-4" data-testid="tab-feedback">
                <MessageSquare className="h-4 w-4 mr-1" />
                Feedback
              </TabsTrigger>
              <TabsTrigger value="trends" className="rounded-full px-4" data-testid="tab-trends">
                <LineChart className="h-4 w-4 mr-1" />
                Trends
              </TabsTrigger>
              <TabsTrigger value="ai-query" className="rounded-full px-4" data-testid="tab-ai-query">
                <Bot className="h-4 w-4 mr-1" />
                AI Query
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview">
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-lg">User Growth</CardTitle>
                    <CardDescription>New signups in the last 30 days</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Today</span>
                        <span className="font-medium">{overview?.users?.today || 0}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">This Week</span>
                        <span className="font-medium">{overview?.users?.this_week || 0}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">This Month</span>
                        <span className="font-medium">{overview?.users?.this_month || 0}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Terms Accepted</span>
                        <span className="font-medium">{overview?.users?.terms_accepted || 0}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-lg">Engagement Summary</CardTitle>
                    <CardDescription>Feature usage overview</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Starred Queries</span>
                        <span className="font-medium">{overview?.engagement?.starred_queries || 0}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Feedback Received</span>
                        <span className="font-medium">{overview?.engagement?.feedback_count || 0}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Ambassadors</span>
                        <span className="font-medium">{overview?.engagement?.ambassadors || 0}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Total Donations</span>
                        <span className="font-medium">{overview?.engagement?.donations || 0}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Users Tab */}
            <TabsContent value="users">
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-lg">Users by Specialty</CardTitle>
                    <CardDescription>Open text entries (case-insensitive)</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <SimpleBarChart 
                      data={userAnalytics?.by_specialty || []} 
                      labelKey="specialty" 
                      valueKey="count" 
                    />
                  </CardContent>
                </Card>

                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-lg">Users by Country</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <SimpleBarChart 
                      data={userAnalytics?.by_country || []} 
                      labelKey="country" 
                      valueKey="count" 
                    />
                  </CardContent>
                </Card>

                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-lg">Users by Role</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {userAnalytics?.by_role?.map((r, i) => (
                        <div key={i} className="flex justify-between items-center">
                          <span className="text-slate-600 capitalize">
                            {r.role === 'trainee' ? '🎓 Trainee' : '👔 Faculty/Staff'}
                          </span>
                          <span className="font-medium bg-slate-100 px-3 py-1 rounded-full text-sm">{r.count}</span>
                        </div>
                      ))}
                      {(!userAnalytics?.by_role || userAnalytics.by_role.length === 0) && (
                        <p className="text-sm text-slate-400">No data yet</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-lg">Users by Profession</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {userAnalytics?.by_profession?.map((p, i) => (
                        <div key={i} className="flex justify-between items-center">
                          <span className="text-slate-600">
                            {p.profession === 'medical_doctor' ? '🩺 Medical Doctor' : '💊 Allied Health'}
                          </span>
                          <span className="font-medium bg-slate-100 px-3 py-1 rounded-full text-sm">{p.count}</span>
                        </div>
                      ))}
                      {(!userAnalytics?.by_profession || userAnalytics.by_profession.length === 0) && (
                        <p className="text-sm text-slate-400">No data yet</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-slate-200 md:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-lg">Recent Signups</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-100">
                            <th className="text-left py-2 text-slate-500 font-medium">Name</th>
                            <th className="text-left py-2 text-slate-500 font-medium">Email</th>
                            <th className="text-left py-2 text-slate-500 font-medium">Specialty</th>
                            <th className="text-left py-2 text-slate-500 font-medium">Role</th>
                            <th className="text-left py-2 text-slate-500 font-medium">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {userAnalytics?.recent_signups?.map((u, i) => (
                            <tr key={i} className="border-b border-slate-50">
                              <td className="py-2 text-slate-900">{u.name}</td>
                              <td className="py-2 text-slate-600">{u.email}</td>
                              <td className="py-2 text-slate-600">{u.specialty || '-'}</td>
                              <td className="py-2 text-slate-500 capitalize">{u.role?.replace('_', ' ') || '-'}</td>
                              <td className="py-2 text-slate-500">{u.created_at?.slice(0, 10)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Searches Tab */}
            <TabsContent value="searches">
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-lg">Searches by Day</CardTitle>
                    <CardDescription>Last 30 days</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <SimpleBarChart 
                      data={searchAnalytics?.queries_by_day?.slice(-14) || []} 
                      labelKey="date" 
                      valueKey="count"
                      maxBars={14}
                    />
                  </CardContent>
                </Card>

                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-lg">Top Users by Searches</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {searchAnalytics?.top_users?.map((u, i) => (
                        <div key={i} className="flex items-center justify-between text-sm">
                          <div>
                            <p className="font-medium text-slate-900">{u.name}</p>
                            <p className="text-xs text-slate-500">{u.specialty || 'No specialty'}</p>
                          </div>
                          <span className="bg-sky-100 text-sky-700 px-2 py-1 rounded-full text-xs font-medium">
                            {u.query_count} searches
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Topics Tab */}
            <TabsContent value="topics">
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Popular Topics
                  </CardTitle>
                  <CardDescription>
                    Based on {topicAnalytics?.total_queries_analyzed || 0} recent queries
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {topicAnalytics?.popular_topics?.map((t, i) => (
                      <span 
                        key={i}
                        className="px-3 py-1.5 bg-slate-100 text-slate-700 rounded-full text-sm"
                        style={{
                          fontSize: `${Math.max(12, Math.min(18, 12 + t.count * 0.5))}px`
                        }}
                      >
                        {t.topic} <span className="text-slate-400">({t.count})</span>
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Engagement Tab */}
            <TabsContent value="engagement">
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Star className="h-5 w-5" />
                      User Ratings
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* App Rating */}
                      <div className="p-3 bg-sky-50 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-slate-900">App Experience</p>
                            <p className="text-xs text-slate-500">How users like the app</p>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-sky-600">
                              {engagementAnalytics?.feedback?.app_rating?.average || 0}
                            </p>
                            <p className="text-xs text-slate-400">
                              {engagementAnalytics?.feedback?.app_rating?.count || 0} ratings
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Accuracy Rating */}
                      <div className="p-3 bg-emerald-50 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-slate-900">Result Accuracy</p>
                            <p className="text-xs text-slate-500">Quality of AI recommendations</p>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-emerald-600">
                              {engagementAnalytics?.feedback?.accuracy_rating?.average || 0}
                            </p>
                            <p className="text-xs text-slate-400">
                              {engagementAnalytics?.feedback?.accuracy_rating?.count || 0} ratings
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Legacy rating */}
                      {engagementAnalytics?.feedback?.total_ratings > 0 && (
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-slate-900">Overall (Legacy)</p>
                              <p className="text-xs text-slate-500">Combined ratings</p>
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-bold text-amber-500">
                                {engagementAnalytics?.feedback?.average_rating || 0}
                              </p>
                              <p className="text-xs text-slate-400">
                                {engagementAnalytics?.feedback?.total_ratings || 0} ratings
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="space-y-2 pt-2 border-t border-slate-100">
                        <p className="text-xs font-medium text-slate-500 uppercase">By Type</p>
                        {engagementAnalytics?.feedback?.by_type?.map((f, i) => (
                          <div key={i} className="flex justify-between text-sm">
                            <span className="text-slate-600 capitalize">{f.type}</span>
                            <span className="font-medium">{f.count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <DollarSign className="h-5 w-5" />
                      Donations
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {engagementAnalytics?.donations?.by_tier?.map((d, i) => (
                        <div key={i} className="flex justify-between items-center text-sm">
                          <span className="text-slate-600 capitalize">{d.tier}</span>
                          <div className="text-right">
                            <span className="font-medium text-slate-900">{d.count} donations</span>
                            <span className="text-green-600 ml-2">${d.revenue}</span>
                          </div>
                        </div>
                      ))}
                      <div className="border-t border-slate-100 pt-2 mt-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">Bypass Clicks ("Not Now")</span>
                          <span className="font-medium">{engagementAnalytics?.donations?.bypass_clicks || 0}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-slate-200 md:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-lg">Feature Usage</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="text-2xl font-bold text-slate-900">
                          {engagementAnalytics?.features?.starred_queries || 0}
                        </p>
                        <p className="text-sm text-slate-500">Starred Queries</p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="text-2xl font-bold text-slate-900">
                          {engagementAnalytics?.features?.cme_exports || 0}
                        </p>
                        <p className="text-sm text-slate-500">CME Exports</p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="text-2xl font-bold text-slate-900">
                          {engagementAnalytics?.features?.shares_by_type?.reduce((a, s) => a + s.count, 0) || 0}
                        </p>
                        <p className="text-sm text-slate-500">Shares</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Retention Tab */}
            <TabsContent value="retention">
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Calendar className="h-5 w-5" />
                      Weekly Cohort Retention
                    </CardTitle>
                    <CardDescription>Users who returned after their signup week</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {retentionAnalytics?.cohorts?.map((c, i) => (
                        <div key={i} className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-slate-900">{c.cohort}</p>
                            <p className="text-xs text-slate-500">{c.users} users signed up</p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-sky-600">{c.retention_rate}%</p>
                            <p className="text-xs text-slate-500">{c.returned} returned</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-lg">Power Users</CardTitle>
                    <CardDescription>Users with 5+ searches</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center py-4">
                      <p className="text-4xl font-bold text-slate-900">
                        {retentionAnalytics?.power_users?.count || 0}
                      </p>
                      <p className="text-lg text-slate-500">
                        {retentionAnalytics?.power_users?.percentage || 0}% of users
                      </p>
                      <div className="mt-4 w-full bg-slate-100 rounded-full h-3">
                        <div 
                          className="bg-emerald-500 h-3 rounded-full transition-all duration-500"
                          style={{ width: `${retentionAnalytics?.power_users?.percentage || 0}%` }}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Alerts Tab */}
            <TabsContent value="alerts">
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Mail className="h-5 w-5" />
                      Email Alerts
                    </CardTitle>
                    <CardDescription>Send notifications to rich@abouthealth.ai</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <h4 className="font-medium text-slate-900 mb-2">Active Alerts</h4>
                        <ul className="text-sm text-slate-600 space-y-1">
                          <li>✅ Daily Summary (manual trigger)</li>
                          <li>✅ New Ambassador ($100+ donation)</li>
                          <li>✅ Bug Reports (immediate)</li>
                          <li>✅ Revenue Milestones ($500 increments)</li>
                          <li>✅ Cost Thresholds ($100 increments)</li>
                        </ul>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={async () => {
                            try {
                              await api.sendTestEmail();
                              toast.success('Test email sent!');
                            } catch (err) {
                              toast.error('Failed to send test email');
                            }
                          }}
                          variant="outline"
                          className="flex-1 rounded-full"
                          data-testid="send-test-email-btn"
                        >
                          <Mail className="h-4 w-4 mr-2" />
                          Send Test Email
                        </Button>
                        <Button
                          onClick={async () => {
                            try {
                              const res = await api.sendDailySummary();
                              toast.success(`Daily summary sent! ${res.data.stats.new_users_today} new users, ${res.data.stats.queries_today} searches`);
                            } catch (err) {
                              toast.error('Failed to send daily summary');
                            }
                          }}
                          className="flex-1 rounded-full bg-slate-900 hover:bg-slate-800"
                          data-testid="send-daily-summary-btn"
                        >
                          <Send className="h-4 w-4 mr-2" />
                          Send Daily Summary
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-lg">Alert Configuration</CardTitle>
                    <CardDescription>Threshold settings</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                        <div>
                          <p className="font-medium text-slate-900">Revenue Alerts</p>
                          <p className="text-xs text-slate-500">Every $500 milestone</p>
                        </div>
                        <span className="text-green-600 font-medium">Active</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-amber-50 rounded-lg">
                        <div>
                          <p className="font-medium text-slate-900">Cost Alerts</p>
                          <p className="text-xs text-slate-500">Every $100 (estimated)</p>
                        </div>
                        <span className="text-amber-600 font-medium">Active</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                        <div>
                          <p className="font-medium text-slate-900">Ambassador Alerts</p>
                          <p className="text-xs text-slate-500">Instant notification</p>
                        </div>
                        <span className="text-purple-600 font-medium">Active</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                        <div>
                          <p className="font-medium text-slate-900">Bug Report Alerts</p>
                          <p className="text-xs text-slate-500">Instant notification</p>
                        </div>
                        <span className="text-red-600 font-medium">Active</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Admins Management Tab */}
            <TabsContent value="admins">
              <AdminManagementPanel />
            </TabsContent>

            {/* Feedback Tab */}
            {/* Feedback Tab */}
            <TabsContent value="feedback">
              <FeedbackPanel />
            </TabsContent>

            {/* Trends Tab */}
            <TabsContent value="trends">
              <TimeSeriesPanel />
            </TabsContent>

            {/* AI Query Tab */}
            <TabsContent value="ai-query">
              <AIQueryPanel />
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboardPage;
